import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Workspace, WorkspaceBase } from '@/types/workspaces.ts'
import {
  getWorkspaces,
  getTasks,
  getTaskOutput,
  createWorkspace as createProjectApi,
  updateWorkspace as updateProjectApi,
  deleteWorkspace as deleteProjectApi,
  duplicateWorkspace as duplicateProjectApi,
  createTask as createTaskApi,
  uploadFileToTask as uploadFileToTaskApi,
  getWorkspaceAssets as getWorkspaceAssetsApi,
  deleteWorkspaceAssets as deleteWorkspaceAssetsApi,
  getFolderContents as getFolderContentsApi,
  shareWorkspace as shareWorkspaceApi,
  getSharedUsers as getSharedUsersApi,
  getSharedWorkspaces,
  getFolders as getFoldersApi,
} from '@/services/api/workspace-api.ts'
import utils from '@/utils/utils'
import { useRoute } from 'vue-router'

export type Query = {
  search?: string
  page?: number
  ordering?: string
}

export const useWorkspaceStore = defineStore('workspaces', () => {
  const route = useRoute()

  // state
  const query = ref<Query>({ page: 1, ordering: '-created_at' })
  const data = ref<Workspace[]>([])
  const loading = ref(false)
  const error = ref<Error | null>(null)
  const selected = ref<string | undefined>(
    route.params.id ? (route.params.id as string) : undefined,
  )
  const next = ref<string | null>(null)
  const previous = ref<string | null>(null)
  const initialized = ref(false)
  const setSelected = (projectId: string) => {
    selected.value = projectId
  }

  // helpers
  const formatWorkspace = (workspace: Workspace): Workspace => ({
    ...workspace,
    created_at: utils.formatDate(workspace.created_at),
  })

  // actions
  const fetchData = async () => {
    try {
      loading.value = true
      error.value = null

      const [ownedWorkspaces, sharedWorkspaces] = await Promise.all([
        getWorkspaces(query.value),
        getSharedWorkspaces(),
      ])

      data.value = [...ownedWorkspaces, ...sharedWorkspaces].map(formatWorkspace)
      initialized.value = true
      return data.value
    } catch (err) {
      error.value = err as Error
    } finally {
      loading.value = false
    }
  }

  const ensureData = async () => {
    if (!initialized.value && !loading.value) {
      await fetchData()
    }
  }

  const createProject = async (project: WorkspaceBase) => {
    try {
      return await createProjectApi(project)
    } catch (err) {
      error.value = err as Error
    }
  }

  const updateProject = async (projectId: string, project: WorkspaceBase) => {
    try {
      const updated = await updateProjectApi(projectId, project)
      data.value = data.value.map((w) => (w.id === projectId ? { ...w, ...project } : w))
      return updated
    } catch (err) {
      error.value = err as Error
    }
  }

  const deleteProject = async (projectId: string) => {
    try {
      return await deleteProjectApi(projectId)
    } catch (err) {
      error.value = err as Error
    }
  }

  const duplicateProject = async (projectId: string) => {
    try {
      return await duplicateProjectApi(projectId)
    } catch (err) {
      error.value = err as Error
    }
  }

  const changeQuery = (newQuery: Query) => {
    query.value = { ...query.value, ...newQuery }
  }

  const fetchTasks = async (params: Record<string, any>) => {
    try {
      return await getTasks(selected.value as string, params)
    } catch (err) {
      error.value = err as Error
    }
  }

  const fetchTask = async (projectId: string, taskId: string) => {
    try {
      error.value = null
      const tasks = await getTasks(projectId)
      return tasks?.find((task) => task.id === taskId)
    } catch (err) {
      error.value = err as Error
    }
  }

  const fetchTaskOutput = async (projectId: string, taskId: string) => {
    try {
      return await getTaskOutput(projectId, taskId)
    } catch (err) {
      error.value = err as Error
    }
  }

  const createTask = async (projectId: string, task: any) => {
    try {
      return await createTaskApi(projectId, task)
    } catch (err) {
      error.value = err as Error
    }
  }

  const uploadFileToTask = async (projectId: string, taskId: string, file: File) => {
    try {
      return await uploadFileToTaskApi(projectId, taskId, file)
    } catch (err) {
      error.value = err as Error
    }
  }

  const getFolderContents = async (params: Record<string, any>) => {
    try {
      return await getFolderContentsApi(selected.value as string, params)
    } catch (err) {
      error.value = err as Error
    }
  }

  const getWorkspaceAssets = async (params: Record<string, any>) => {
    try {
      return await getWorkspaceAssetsApi(selected.value as string, params)
    } catch (err) {
      error.value = err as Error
    }
  }

  const deleteWorkspaceAssets = async (assetId: string) => {
    try {
      return await deleteWorkspaceAssetsApi(selected.value as string, assetId)
    } catch (err) {
      error.value = err as Error
    }
  }

  const shareWorkspace = async (workspaceId: string, users: string[]) => {
    try {
      return await shareWorkspaceApi(workspaceId, users)
    } catch (err) {
      error.value = err as Error
    }
  }

  const getSharedUsers = async (workspaceId: string) => {
    try {
      return await getSharedUsersApi(workspaceId)
    } catch (err) {
      error.value = err as Error
    }
  }

  const getFolders = async (params: Record<string, any>) => {
    try {
      return await getFoldersApi(selected.value as string, params)
    } catch (err) {
      error.value = err as Error
    }
  }

  return {
    // state
    query,
    data,
    loading,
    error,
    selected,
    setSelected,
    next,
    previous,
    initialized,
    // actions
    fetchData,
    ensureData,
    changeQuery,
    fetchTasks,
    fetchTask,
    fetchTaskOutput,
    createProject,
    updateProject,
    deleteProject,
    duplicateProject,
    createTask,
    uploadFileToTask,
    getFolderContents,
    deleteWorkspaceAssets,
    getWorkspaceAssets,
    shareWorkspace,
    getSharedUsers,
    getFolders,
  }
})
